/**
 * File Operations for Version 2.0 Projects
 * 
 * Upload/Download-Funktionalität mit File System Access API und JSZip
 */

import JSZip from 'jszip';
import { vfs } from './VirtualFileSystem';
import { FileDefinition } from './types';
import { getDomainCfg } from '../../core/state';

/**
 * Liest eine hochgeladene Datei als Text
 */
export async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsText(file);
  });
}

/**
 * Liest eine hochgeladene Datei als ArrayBuffer (für Binärdateien)
 */
export async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Bestimmt die Sprache basierend auf der Dateiendung
 */
export function getLanguageFromFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();

  switch (ext) {
    case 'html':
    case 'htm':
      return 'html';
    case 'css':
      return 'css';
    case 'js':
    case 'mjs':
      return 'javascript';
    case 'ts':
      return 'typescript';
    case 'jsx':
      return 'javascriptreact';
    case 'tsx':
      return 'typescriptreact';
    case 'json':
      return 'json';
    case 'md':
      return 'markdown';
    case 'py':
      return 'python';
    case 'xml':
      return 'xml';
    case 'svg':
      return 'xml';
    case 'txt':
    default:
      return 'plaintext';
  }
}

/**
 * Prüft ob eine Datei Text ist (nicht binär)
 */
export function isTextFile(filename: string): boolean {
  const textExtensions = [
    'html', 'htm', 'css', 'js', 'mjs', 'ts', 'jsx', 'tsx',
    'json', 'md', 'txt', 'xml', 'svg', 'py', 'java', 'c', 'cpp', 'h',
    'sh', 'bash', 'yml', 'yaml', 'toml', 'ini', 'cfg', 'data'
  ];
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return textExtensions.includes(ext);
}

/**
 * Prüft ob eine Datei eine unterstützte Mediendatei ist
 */
export function isMediaFile(filename: string): boolean {
  const mediaExtensions = [
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'mp3', 'wav', 'mp4'
  ];
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return mediaExtensions.includes(ext);
}

/**
 * Liest eine hochgeladene Datei als Data URL (Basis64)
 */
export async function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/**
 * Bestimmt den MimeType aus der Dateiendung
 */
export function getMimeTypeFromFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'gif': return 'image/gif';
    case 'webp': return 'image/webp';
    case 'mp3': return 'audio/mpeg';
    case 'wav': return 'audio/wav';
    case 'mp4': return 'video/mp4';
    case 'svg': return 'image/svg+xml';
    default: return 'application/octet-stream';
  }
}

/**
 * Verarbeitet eine hochgeladene Datei und fügt sie zum Projekt hinzu
 */
export interface UploadResult {
  success: boolean;
  file?: FileDefinition;
  content?: string;
  error?: string;
}

export async function processUploadedFile(
  file: File,
  projectId: string,
  existingFiles: FileDefinition[]
): Promise<UploadResult> {
  try {
    // Prüfe ob Datei schon existiert
    const existingFile = existingFiles.find(f =>
      f.path === file.name || f.name === file.name
    );

    if (existingFile) {
      return {
        success: false,
        error: `Datei "${file.name}" existiert bereits im Projekt.`
      };
    }

    // Nur Textdateien und Mediendateien unterstützen
    const isText = isTextFile(file.name);
    const isMedia = isMediaFile(file.name);

    if (!isText && !isMedia) {
      return {
        success: false,
        error: `Dateityp wird nicht unterstützt. Nur Text- und gängige Mediendateien (Bilder, Audio, Video) sind erlaubt.`
      };
    }

    // Dateigröße prüfen (bei Mediendateien)
    if (isMedia) {
      const mbSizeLimit = getDomainCfg().viewSettings?.features?.vfsMaxFileSizeMb || 2;
      const byteLimit = mbSizeLimit * 1024 * 1024;
      if (file.size > byteLimit) {
        return {
          success: false,
          error: `Datei ist zu groß. Die maximale Dateigröße beträgt ${mbSizeLimit} MB.`
        };
      }
    }

    // Datei-Inhalt lesen
    let content = '';
    if (isMedia) {
      content = await readFileAsDataURL(file);
    } else {
      content = await readFileAsText(file);
    }
    const language = getLanguageFromFilename(file.name);

    // FileDefinition erstellen
    const newFile: FileDefinition = {
      path: file.name,
      name: file.name,
      language,
      active: false // Neue Dateien nicht automatisch öffnen
    };

    // In VFS speichern (fire-and-forget, blockiert nicht die UI)
    const fullPath = vfs.getProjectRoot(projectId) + '/' + file.name;
    vfs.writeFile(fullPath, content)
      .then(() => console.log(`[FileOperations] File persisted to VFS: ${fullPath}`))
      .catch((vfsError) => console.warn('[FileOperations] VFS write failed:', vfsError));

    return {
      success: true,
      file: newFile,
      content
    };
  } catch (error: any) {
    console.error('[FileOperations] Upload failed:', error);
    return {
      success: false,
      error: error.message || 'Upload fehlgeschlagen'
    };
  }
}

/**
 * Verarbeitet mehrere hochgeladene Dateien
 */
export async function processMultipleUploads(
  files: File[],
  projectId: string,
  existingFiles: FileDefinition[]
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];

  for (const file of files) {
    const result = await processUploadedFile(file, projectId, existingFiles);
    results.push(result);

    // Bei erfolgreicher Upload zur existingFiles hinzufügen für nächste Prüfung
    if (result.success && result.file) {
      existingFiles = [...existingFiles, result.file];
    }
  }

  return results;
}

/**
 * Erstellt ein ZIP-Archiv aus den Projektdateien
 */
export async function createProjectZip(
  projectId: string,
  files: FileDefinition[],
  fileContents: Map<string, string>
): Promise<Blob> {
  const zip = new JSZip();

  for (const file of files) {
    const content = fileContents.get(file.path);
    if (content !== undefined) {
      // Füge Datei zum ZIP hinzu (ohne führenden Slash)
      const zipPath = file.path.startsWith('/') ? file.path.slice(1) : file.path;

      if (typeof content === 'string' && content.startsWith('data:') && content.includes(';base64,')) {
        const base64Data = content.split(',')[1];
        zip.file(zipPath, base64Data, { base64: true });
      } else {
        zip.file(zipPath, content);
      }
    }
  }

  // ZIP generieren
  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });

  return blob;
}

/**
 * Lädt ein ZIP-Archiv herunter
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Lädt das Projekt als ZIP herunter
 */
export async function downloadProjectAsZip(
  projectId: string,
  files: FileDefinition[],
  fileContents: Map<string, string>
): Promise<void> {
  try {
    const blob = await createProjectZip(projectId, files, fileContents);
    const filename = `${projectId}.zip`;
    downloadBlob(blob, filename);
    console.log(`[FileOperations] Project downloaded as ${filename}`);
  } catch (error) {
    console.error('[FileOperations] Download failed:', error);
    throw error;
  }
}

/**
 * Importiert ein ZIP-Archiv in das Projekt
 */
export interface ZipImportResult {
  success: boolean;
  files: FileDefinition[];
  contents: Map<string, string>;
  errors: string[];
}

export async function importProjectFromZip(
  file: File,
  projectId: string
): Promise<ZipImportResult> {
  const result: ZipImportResult = {
    success: false,
    files: [],
    contents: new Map(),
    errors: []
  };

  try {
    const arrayBuffer = await readFileAsArrayBuffer(file);
    const zip = await JSZip.loadAsync(arrayBuffer);

    // Alle Dateien aus dem ZIP extrahieren
    const promises: Promise<void>[] = [];

    zip.forEach((relativePath, zipEntry) => {
      // Überspringe Verzeichnisse und versteckte Dateien
      if (zipEntry.dir || relativePath.startsWith('.') || relativePath.includes('/__MACOSX/')) {
        return;
      }

      const promise = (async () => {
        try {
          // Nur Text- und Mediendateien
          const isText = isTextFile(relativePath);
          const isMedia = isMediaFile(relativePath);

          if (!isText && !isMedia) {
            result.errors.push(`Übersprungen (nicht unterstützt): ${relativePath}`);
            return;
          }

          let content = '';
          if (isMedia) {
            const base64Content = await zipEntry.async('base64');
            const mimeType = getMimeTypeFromFilename(relativePath);
            content = `data:${mimeType};base64,${base64Content}`;
          } else {
            content = await zipEntry.async('string');
          }
          const language = getLanguageFromFilename(relativePath);

          const fileDef: FileDefinition = {
            path: relativePath,
            name: relativePath.split('/').pop() || relativePath,
            language,
            active: false
          };

          result.files.push(fileDef);
          result.contents.set(relativePath, content);
        } catch (err: any) {
          result.errors.push(`Fehler bei ${relativePath}: ${err.message}`);
        }
      })();

      promises.push(promise);
    });

    await Promise.all(promises);

    // In VFS speichern (falls initialisiert)
    if (vfs.isInitialized()) {
      await vfs.saveProjectFiles(projectId, result.contents);
    }

    result.success = result.files.length > 0;
    console.log(`[FileOperations] Imported ${result.files.length} files from ZIP`);

  } catch (error: any) {
    console.error('[FileOperations] ZIP import failed:', error);
    result.errors.push(error.message || 'ZIP-Import fehlgeschlagen');
  }

  return result;
}

/**
 * Erstellt eine neue leere Datei im Projekt
 */
export function createNewFile(
  filename: string,
  existingFiles: FileDefinition[]
): { success: boolean; file?: FileDefinition; error?: string } {
  // Validiere Dateinamen
  if (!filename || filename.trim() === '') {
    return { success: false, error: 'Dateiname darf nicht leer sein.' };
  }

  // Bereinige Dateinamen
  const cleanName = filename.trim().replace(/[<>:"|?*]/g, '');

  // Prüfe ob Datei schon existiert
  const exists = existingFiles.some(f =>
    f.path === cleanName || f.name === cleanName
  );

  if (exists) {
    return { success: false, error: `Datei "${cleanName}" existiert bereits.` };
  }

  const language = getLanguageFromFilename(cleanName);

  const newFile: FileDefinition = {
    path: cleanName,
    name: cleanName,
    language,
    active: false
  };

  return { success: true, file: newFile };
}

